import {
  XCircleIcon,
  InformationCircleIcon,
  ExclamationIcon,
  CheckCircleIcon,
} from '@heroicons/react/solid'
import { ComponentProps, FC } from 'react'
import { cn } from 'utilities/StringUtils'

interface AlertProps extends ComponentProps<'div'> {
  border?: boolean
  defaultMessage?: string
  listMessages?: string[]
  title?: string
  variant?: 'success' | 'warning' | 'danger' | 'info'
}

export const Alert: FC<AlertProps> = ({
  variant = 'danger',
  title,
  defaultMessage = 'Something went wrong!',
  listMessages = [],
  border = false,
  children,
  ...props
}) => {
  const displayTitle = title ?? defaultMessage

  return (
    <div
      {...props}
      className={cn(
        'flex rounded-xl overflow-hidden p-4',
        {
          success: 'bg-green-50',
          warning: 'bg-yellow-50',
          info: 'bg-blue-50',
          danger: 'bg-red-50',
        }[variant],
        {
          'border border-green-400': border && variant === 'success',
          'border border-yellow-400': border && variant === 'warning',
          'border border-blue-400': border && variant === 'info',
          'border border-red-400': border && variant === 'danger',
        },
        props.className
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          'size-5',
          {
            success: 'text-green-400',
            warning: 'text-yellow-400',
            info: 'text-blue-400',
            danger: 'text-red-400',
          }[variant]
        )}
        variant={variant}
      />

      <div className="ml-3 flex-grow">
        {displayTitle && (
          <h3
            className={cn(
              'text-sm font-medium',
              {
                success: 'text-green-800',
                warning: 'text-yellow-800',
                info: 'text-blue-800',
                danger: 'text-red-800',
              }[variant]
            )}
          >
            {displayTitle}
          </h3>
        )}

        {(children || listMessages.length > 0) && (
          <div
            className={cn(
              'text-sm',
              { 'mt-2': !!displayTitle },
              {
                success: 'text-green-700',
                warning: 'text-yellow-700',
                info: 'text-blue-700',
                danger: 'text-red-700',
              }[variant]
            )}
          >
            {children}

            {listMessages.length > 0 && (
              <ul className="list-disc pl-5 space-y-1">
                {listMessages.map((message, i) => (
                  <li key={i}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface IconProps extends ComponentProps<'svg'> {
  variant?: AlertProps['variant']
}

const Icon: FC<IconProps> = ({ variant, ...props }) => {
  switch (variant) {
    case 'success':
      return <CheckCircleIcon {...props} />
    case 'warning':
      return <ExclamationIcon {...props} />
    case 'danger':
      return <XCircleIcon {...props} />
    case 'info':
      return <InformationCircleIcon {...props} />
  }
}
